import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import format from 'string-template';

import { IconButton, Tooltip } from '@mui/material';
import {
  HighlightOff as DeleteIcon, Reorder, InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { Draggable } from 'react-smooth-dnd';

import defaultMealImage from '../../../../../assets/defaultMealImage.svg';
import { RecipeTag, getViolatingIngredientNames } from '../../../../utils/meals';
import { AddButton } from '../../../../../components/Button/ActionButtons';
import { WarningTag } from '../../../../../components/Tags';

import { getColorBasedOnDeviation, CreatedBy } from './utils';
import {
  Container,
  MealImage,
  MealName,
  StyledIconButton,
  MealCell,
  MealWrapper,
  DislikedRow,
} from './styles';
import texts from './texts';

const mealOptionValues = Object.values(RecipeTag).map((tag) => ({
  value: tag,
  label: texts.meal[tag],
}));

const MealItem = ({
  recipe: {
    id,
    image,
    name,
    totalCalories,
    protein,
    proteinPercentage,
    carbs,
    carbsPercentage,
    fat,
    fatPercentage,
    preparationTime,
    tags,
    allergenTags,
    createdBy,
    ingredients,
  },
  bucketIndex,
  bucketSummary: {
    calories: targetCalories,
    protein: targetProtein,
    carbs: targetCarbs,
    fat: targetFat,
  },
  handleDelete,
  handleView,
  handleSave,
  allowDrag,
  allowDelete,
  servings,
  index,
  enableColorCoding,
  excludedIngredients,
}) => {
  const imageUrl = image || defaultMealImage;

  const violatingIngredients = useMemo(
    () => getViolatingIngredientNames(ingredients, excludedIngredients),
    [ingredients, excludedIngredients],
  );

  const recipeCalsAndMacros = useMemo(() => ({
    calories: Math.round(totalCalories * servings),
    protein: Math.round(protein * servings),
    carbs: Math.round(carbs * servings),
    fat: Math.round(fat * servings),
  }), [
    totalCalories,
    protein,
    carbs,
    fat,
    servings,
  ]);

  const recipeColorCode = useMemo(() => {
    if (enableColorCoding) {
      return {
        calories: getColorBasedOnDeviation(recipeCalsAndMacros.calories, targetCalories),
        protein: getColorBasedOnDeviation(recipeCalsAndMacros.protein, targetProtein),
        carbs: getColorBasedOnDeviation(recipeCalsAndMacros.carbs, targetCarbs),
        fat: getColorBasedOnDeviation(recipeCalsAndMacros.fat, targetFat),
      };
    }
    return {};
  }, [
    enableColorCoding,
    recipeCalsAndMacros,
    targetCalories,
    targetProtein,
    targetCarbs,
    targetFat,
  ]);

  return (
    <Draggable key={index}>
      <MealWrapper>
        <Container>
          <MealCell>
            {allowDrag && (
              <IconButton className="drag-handle">
                <Reorder />
              </IconButton>
            )}
          </MealCell>
          <MealCell onClick={() => handleView(id, servings)}>
            <MealImage src={imageUrl} />
            <MealName>{name}</MealName>
          </MealCell>
          <MealCell $color={recipeColorCode.calories}>
            {`${recipeCalsAndMacros.calories} ${texts.caloriesUnit}`}
          </MealCell>
          <MealCell $color={recipeColorCode.protein}>
            {`${recipeCalsAndMacros.protein}g (${proteinPercentage}%)`}
          </MealCell>
          <MealCell $color={recipeColorCode.carbs}>
            {`${recipeCalsAndMacros.carbs}g (${carbsPercentage}%)`}
          </MealCell>
          <MealCell $color={recipeColorCode.fat}>
            {`${recipeCalsAndMacros.fat}g (${fatPercentage}%)`}
          </MealCell>
          <MealCell>
            {allergenTags.length ? allergenTags.join(', ') : texts.empty}
          </MealCell>
          <MealCell>
            {format(texts.prepTime, { preparationTime })}
          </MealCell>
          <MealCell>
            {tags
              .map((tag) => {
                const meal = mealOptionValues.find((option) => option.value === tag);
                return meal ? meal.label : '';
              })
              .join(', ')}
          </MealCell>
          <MealCell>
            {createdBy === CreatedBy.AI_FRIEND && (
              <AddButton
                variant="muted"
                onClick={() => handleSave(id, bucketIndex)}
                $fitToContent
              >
                {texts.save}
              </AddButton>
            )}
          </MealCell>
          <MealCell>
            {allowDelete && (
              <StyledIconButton size="small" onClick={() => handleDelete(id, bucketIndex)}>
                <DeleteIcon fontSize="inherit" />
              </StyledIconButton>
            )}
          </MealCell>
        </Container>
        {violatingIngredients.length > 0 && (
          <DislikedRow>
            <Tooltip
              title={format(texts.violatingIngredients, { ingredients: violatingIngredients.join(', ') })}
              placement="top"
              arrow
            >
              <WarningTag>
                <InfoIcon fontSize="inherit" />
                {texts.containsDislikedIngredients}
              </WarningTag>
            </Tooltip>
          </DislikedRow>
        )}
      </MealWrapper>
    </Draggable>
  );
};

MealItem.propTypes = {
  recipe: PropTypes.object.isRequired,
  bucketIndex: PropTypes.number.isRequired,
  bucketSummary: PropTypes.object.isRequired,
  allowDelete: PropTypes.bool.isRequired,
  handleDelete: PropTypes.func,
  handleView: PropTypes.func,
  handleSave: PropTypes.func,
  servings: PropTypes.number,
  index: PropTypes.number.isRequired,
  allowDrag: PropTypes.bool,
  enableColorCoding: PropTypes.bool,
  excludedIngredients: PropTypes.array,
};

MealItem.defaultProps = {
  handleDelete: () => {},
  handleView: () => {},
  handleSave: () => {},
  servings: 1,
  allowDrag: false,
  enableColorCoding: false,
  excludedIngredients: [],
};

export default observer(MealItem);
